import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { StatGroupComponent, StatItem } from '../../components/stat-group/stat-group.component';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { TestStepService } from '../../services/test-step.service';
import { ProjectRoleService } from '../../services/project-role.service';
import { SavedViewService } from '../../services/saved-view.service';
import { STEP_CATEGORIES, StepCategory, TestStep, TestStepRunStatus } from '../../models/test-case.model';
import { SavedView } from '../../models/saved-view.model';
import {
  FilterOption,
  FilterChange,
  TestStepFilterBarComponent,
  TestStepFilterKey,
} from './test-step-filter-bar/test-step-filter-bar.component';
import { SavedViewOption } from '../../shared/saved-views/saved-views-menu.component';
import { buildTestStepSavedViewFiltersJson, parseTestStepSavedViewFilters } from './test-step.utils';
import { ListFocusScrollDirective } from '../../shared/scroll-container/list-focus-scroll.directive';

interface TestStepRow {
  id: string;
  /** Kurzform der UUID als ID-Pill (#xxxxxxxx). */
  shortId: string;
  title: string;
  category: StepCategory | null;
  categoryLabel: string;
  categoryClass: string;
  status: TestStepRunStatus;
  statusLabel: string;
  statusClass: string;
  usageCount: number;
  authorName: string | null;
}

// Feste Reihenfolge für die Filter-Optionen, damit die Dropdowns unabhängig
// von der Datenreihenfolge stabil sind (analog test-case.component).
const CATEGORY_ORDER: readonly StepCategory[] = STEP_CATEGORIES;
const STATUS_ORDER: readonly TestStepRunStatus[] = ['DRAFT', 'RUNNING', 'PASSED', 'FAILED'];

const CATEGORY_RANK: Record<StepCategory, number> = {
  SETUP: 0,
  AUTHENTICATION: 1,
  VERIFICATION: 2,
  TEARDOWN: 3,
};
const STATUS_RANK: Record<TestStepRunStatus, number> = { DRAFT: 0, RUNNING: 1, PASSED: 2, FAILED: 3 };

export type TestStepSortCol = 'shortId' | 'title' | 'category' | 'status' | 'usageCount' | 'authorName';
type SortDir = 'asc' | 'desc';
interface TableSort {
  col: TestStepSortCol | null;
  dir: SortDir;
}

// Das Bundle (page-testschritte.jsx:218-222) zeigt Gesamt · Aktiv · Entwürfe mit
// Aktiv = alles außer Entwurf. Bewusste Abweichung (NEX-1382): der Status ist ein
// Ausführungs-Status, kein Lebenszyklus-Status. Deshalb eine Kachel je Status, damit
// Fehlgeschlagene sichtbar sind und die Kacheln sich zur Gesamtzahl summieren.
const STAT_TILE_ORDER: readonly TestStepRunStatus[] = ['PASSED', 'FAILED', 'RUNNING', 'DRAFT'];
const STAT_TILES: Record<TestStepRunStatus, { labelKey: string; color: string }> = {
  PASSED: { labelKey: 'testSteps.stats.passed', color: 'var(--to-green-fg)' },
  FAILED: { labelKey: 'testSteps.stats.failed', color: 'var(--to-red-fg)' },
  RUNNING: { labelKey: 'testSteps.stats.running', color: 'var(--to-blue-fg)' },
  DRAFT: { labelKey: 'testSteps.stats.drafts', color: 'var(--to-neutral-fg)' },
};

@Component({
  selector: 'app-test-step',
  imports: [
    ListFocusScrollDirective,
    TranslatePipe,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    ProjectNavComponent,
    PageHeaderComponent,
    StatGroupComponent,
    AvatarComponent,
    SearchBarComponent,
    ConfirmDialogComponent,
    TestStepFilterBarComponent,
  ],
  templateUrl: './test-step.component.html',
  styleUrl: './test-step.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestStepComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly testStepService = inject(TestStepService);
  private readonly savedViewService = inject(SavedViewService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly projectRoleService = inject(ProjectRoleService);

  readonly isReadOnly = this.projectRoleService.isReadOnly;

  private readonly currentLang = toSignal(this.translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this.translate.currentLang,
  });

  readonly projectId: string;

  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly deleteError = signal(false);
  readonly deleting = signal(false);
  readonly searchTerm = signal('');

  readonly selectedCategory = signal<string[]>([]);
  readonly selectedStatus = signal<string[]>([]);
  readonly selectedAuthor = signal<string[]>([]);

  readonly savedViews = signal<SavedView[]>([]);
  readonly activeSavedViewName = signal<string | null>(null);
  readonly savedViewsError = signal<string | null>(null);

  readonly savedViewsOptions = computed<SavedViewOption[]>(() =>
    this.savedViews().map((v) => ({ name: v.name, summary: this.summarizeSavedView(v) })),
  );

  readonly showDeleteModal = signal(false);
  readonly rowToDelete = signal<TestStepRow | null>(null);

  private readonly steps = signal<TestStep[]>([]);

  readonly rows = computed<TestStepRow[]>(() => {
    // Sprache lesen, damit Pill-/Label-Strings bei Sprachwechsel neu berechnet werden.
    this.currentLang();
    return this.steps().map((s) => this.toRow(s));
  });

  private readonly statusCounts = computed<Record<TestStepRunStatus, number>>(() => {
    const all = this.steps();
    return Object.fromEntries(
      STATUS_ORDER.map((status) => [status, all.filter((s) => s.status === status).length]),
    ) as Record<TestStepRunStatus, number>;
  });

  readonly statItems = computed<StatItem[]>(() => {
    this.currentLang();
    const counts = this.statusCounts();
    return [
      { label: this.translate.instant('testSteps.stats.total'), value: this.steps().length },
      ...STAT_TILE_ORDER.map((status) => ({
        label: this.translate.instant(STAT_TILES[status].labelKey),
        value: counts[status],
        color: STAT_TILES[status].color,
      })),
    ];
  });

  readonly statsAriaLabel = computed(() => {
    this.currentLang();
    const counts = this.statusCounts();
    return this.translate.instant('testSteps.stats.ariaLabel', {
      total: this.steps().length,
      passed: counts.PASSED,
      failed: counts.FAILED,
      running: counts.RUNNING,
      drafts: counts.DRAFT,
    });
  });

  readonly categoryOptions = computed<FilterOption[]>(() => {
    this.currentLang();
    return this.enumOptions(
      this.rows().map((r) => r.category),
      CATEGORY_ORDER,
      'testSteps.category',
    );
  });

  readonly statusOptions = computed<FilterOption[]>(() => {
    this.currentLang();
    return this.enumOptions(
      this.rows().map((r) => r.status),
      STATUS_ORDER,
      'testSteps.status',
    );
  });

  readonly authorOptions = computed<FilterOption[]>(() => toLabelOptions(this.rows().map((r) => r.authorName)));

  private enumOptions<T extends string>(
    values: readonly (T | null)[],
    order: readonly T[],
    i18nPrefix: string,
  ): FilterOption[] {
    const present = new Set(values);
    return order
      .filter((v) => present.has(v))
      .map((v) => ({ value: v, label: this.translate.instant(`${i18nPrefix}.${v}`) }));
  }

  readonly filteredRows = computed<TestStepRow[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const category = this.selectedCategory();
    const status = this.selectedStatus();
    const author = this.selectedAuthor();

    return this.rows().filter((row) => {
      if (term && !row.title.toLowerCase().includes(term) && !row.id.toLowerCase().includes(term)) {
        return false;
      }
      if (category.length && (row.category === null || !category.includes(row.category))) return false;
      if (status.length && !status.includes(row.status)) return false;
      if (author.length && (row.authorName === null || !author.includes(row.authorName))) return false;
      return true;
    });
  });

  readonly total = computed(() => this.filteredRows().length);

  readonly tableSort = signal<TableSort>({ col: null, dir: 'asc' });

  readonly sortedRows = computed<TestStepRow[]>(() => sortRows(this.filteredRows(), this.tableSort()));

  readonly hasResults = computed(() => this.total() > 0);

  // ngx-translate läuft hier ohne ICU/Plural-Compiler — Singular/Plural per Key.
  readonly resultCountKey = computed(() =>
    this.total() === 1 ? 'testSteps.resultCountOne' : 'testSteps.resultCountOther',
  );

  constructor() {
    this.projectId = this.route.snapshot.params['projectId'];
  }

  ngOnInit(): void {
    this.loadSteps();
    this.loadSavedViews();
  }

  private loadSavedViews(): void {
    this.savedViewService.list(this.projectId, 'TEST_STEPS').subscribe({
      next: (views) => this.savedViews.set(views),
      error: () => this.savedViews.set([]),
    });
  }

  private summarizeSavedView(view: SavedView): string {
    const filters = parseTestStepSavedViewFilters(view.filtersJson);
    const parts = [
      this.describeEnumFilter(filters.category, 'testSteps.category'),
      this.describeEnumFilter(filters.status, 'testSteps.status'),
      this.describeFreetextFilter(filters.author),
    ].filter((s): s is string => !!s);
    if (parts.length === 0) {
      return this.translate.instant('testSteps.savedViews.summaryAll');
    }
    return parts.join(' · ');
  }

  private describeEnumFilter(values: string[], i18nPrefix: string): string | null {
    // Single-Select: nur den ersten Wert beschreiben, damit die Zusammenfassung
    // nicht mehr verspricht, als das Dropdown anzeigt (Alt-Ansichten haben ggf.
    // mehrere Werte; die werden beim Laden auf [0] gekuerzt).
    if (values.length === 0) return null;
    return this.translate.instant(`${i18nPrefix}.${values[0]}`);
  }

  private describeFreetextFilter(values: string[]): string | null {
    return values.length === 0 ? null : values[0];
  }

  onSavedViewSelected(name: string): void {
    const view = this.savedViews().find((v) => v.name === name);
    if (!view) return;
    const filters = parseTestStepSavedViewFilters(view.filtersJson);
    this.searchTerm.set(filters.query ?? '');
    // Alt-Ansichten (vor NEX-1054-Follow-up) konnten mehrere Werte je Facette
    // enthalten. Single-Select zeigt jetzt aber nur den ersten Wert — ohne
    // slice(0,1) blieben zusaetzliche Werte intern aktiv und die Tabelle
    // wuerde stumm mehr Zeilen zeigen als das Dropdown erklaert.
    this.selectedCategory.set(filters.category.slice(0, 1));
    this.selectedStatus.set(filters.status.slice(0, 1));
    this.selectedAuthor.set(filters.author.slice(0, 1));
    this.activeSavedViewName.set(view.name);
  }

  onSavedViewSaved(name: string): void {
    const filtersJson = buildTestStepSavedViewFiltersJson({
      query: this.searchTerm() || null,
      category: this.selectedCategory(),
      status: this.selectedStatus(),
      author: this.selectedAuthor(),
    });
    this.savedViewService.upsert(this.projectId, { name, context: 'TEST_STEPS', filtersJson }).subscribe({
      next: (saved) => {
        this.savedViews.update((list) => {
          const rest = list.filter((v) => v.name !== saved.name);
          return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
        });
        this.activeSavedViewName.set(saved.name);
        this.savedViewsError.set(null);
      },
      error: () => this.savedViewsError.set(this.translate.instant('testSteps.savedViews.saveError')),
    });
  }

  /**
   * Das Loeschen selbst uebernimmt der generische SavedViewsMenuComponent (NEX-786) —
   * hier wird nur noch der lokale State nach einem erfolgreichen Loeschvorgang bereinigt.
   */
  onSavedViewDeleted(name: string): void {
    this.savedViews.update((list) => list.filter((v) => v.name !== name));
    if (this.activeSavedViewName() === name) {
      this.activeSavedViewName.set(null);
    }
  }

  onSavedViewDeleteFailed(): void {
    this.savedViewsError.set(this.translate.instant('testSteps.savedViews.deleteError'));
  }

  private clearActiveView(): void {
    if (this.activeSavedViewName() !== null) {
      this.activeSavedViewName.set(null);
    }
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.clearActiveView();
  }

  onFilterChange({ key, value }: FilterChange): void {
    this.filterSignal(key).set(value ? [value] : []);
    this.clearActiveView();
  }

  onResetFilters(): void {
    this.selectedCategory.set([]);
    this.selectedStatus.set([]);
    this.selectedAuthor.set([]);
    this.searchTerm.set('');
    this.clearActiveView();
  }

  private filterSignal(key: TestStepFilterKey) {
    switch (key) {
      case 'category':
        return this.selectedCategory;
      case 'status':
        return this.selectedStatus;
      case 'author':
        return this.selectedAuthor;
    }
  }

  private pruneStaleFilters(): void {
    const keep = (signal: typeof this.selectedStatus, options: FilterOption[]): void => {
      const available = new Set(options.map((o) => o.value));
      const next = signal().filter((v) => available.has(v));
      if (next.length !== signal().length) signal.set(next);
    };
    keep(this.selectedCategory, this.categoryOptions());
    keep(this.selectedStatus, this.statusOptions());
    keep(this.selectedAuthor, this.authorOptions());
  }

  sortIcon(col: TestStepSortCol): string {
    const s = this.tableSort();
    if (s.col !== col) return 'unfold_more';
    return s.dir === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  sortAria(col: TestStepSortCol): 'ascending' | 'descending' | 'none' {
    const s = this.tableSort();
    if (s.col !== col) return 'none';
    return s.dir === 'asc' ? 'ascending' : 'descending';
  }

  sortBy(col: TestStepSortCol): void {
    const current = this.tableSort();
    this.tableSort.set({ col, dir: current.col === col && current.dir === 'asc' ? 'desc' : 'asc' });
  }

  onCreate(): void {
    this.router.navigate(['/projects', this.projectId, 'test-steps', 'new']);
  }

  onEdit(row: TestStepRow): void {
    this.router.navigate(['/projects', this.projectId, 'test-steps', row.id, 'edit']);
  }

  onDeleteRequest(row: TestStepRow): void {
    this.rowToDelete.set(row);
    this.showDeleteModal.set(true);
  }

  onCancelDelete(): void {
    this.showDeleteModal.set(false);
    this.rowToDelete.set(null);
  }

  onConfirmDelete(): void {
    const row = this.rowToDelete();
    if (!row) return;
    this.deleteError.set(false);
    this.deleting.set(true);
    this.testStepService
      .deleteLibrary(this.projectId, row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.steps.set(this.steps().filter((s) => s.id !== row.id));
          this.pruneStaleFilters();
          this.deleting.set(false);
          this.showDeleteModal.set(false);
          this.rowToDelete.set(null);
        },
        error: () => {
          this.deleting.set(false);
          this.showDeleteModal.set(false);
          this.rowToDelete.set(null);
          this.deleteError.set(true);
        },
      });
  }

  readonly deleteMessage = computed(() => {
    this.currentLang();
    const row = this.rowToDelete();
    if (!row) return '';
    const base = this.translate.instant('testSteps.deleteMessage', { title: row.title });
    if (row.usageCount > 0) {
      return `${base} ${this.translate.instant('testSteps.deleteUsageHint', { count: row.usageCount })}`;
    }
    return base;
  });

  private loadSteps(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.testStepService
      .listLibrary(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (steps) => {
          this.steps.set(steps);
          this.pruneStaleFilters();
          this.loading.set(false);
        },
        error: () => {
          this.steps.set([]);
          this.pruneStaleFilters();
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  private toRow(step: TestStep): TestStepRow {
    const category = step.category ?? null;
    const title = (step.title ?? '').trim();
    return {
      id: step.id,
      // Menschenlesbarer, backend-generierter Key (TS-###, NEX-654) roh als ID-Pill.
      shortId: step.key ?? '',
      title: title || this.translate.instant('testSteps.noTitle'),
      category,
      categoryLabel: category ? this.translate.instant(`testSteps.category.${category}`) : '',
      categoryClass: categoryPillClass(category),
      status: step.status,
      statusLabel: this.translate.instant(`testSteps.status.${step.status}`),
      statusClass: statusPillClass(step.status),
      usageCount: step.usageCount ?? 0,
      authorName: step.assigneeName?.trim() || null,
    };
  }
}

function categoryPillClass(category: StepCategory | null): string {
  return category ? `ts-pill ts-pill--cat-${category.toLowerCase()}` : '';
}

function statusPillClass(status: TestStepRunStatus): string {
  return `ts-pill ts-pill--status-${status.toLowerCase()}`;
}

function sortRows(rows: TestStepRow[], sort: TableSort): TestStepRow[] {
  const { col, dir } = sort;
  if (!col) return rows;
  const copy = [...rows];
  copy.sort((a, b) => {
    let cmp: number;
    switch (col) {
      case 'category':
        cmp = categoryRank(a.category) - categoryRank(b.category);
        break;
      case 'status':
        cmp = STATUS_RANK[a.status] - STATUS_RANK[b.status];
        break;
      case 'usageCount':
        cmp = a.usageCount - b.usageCount;
        break;
      case 'shortId':
        cmp = a.shortId.localeCompare(b.shortId, undefined, { numeric: true, sensitivity: 'base' });
        break;
      default:
        cmp = (a[col] ?? '').localeCompare(b[col] ?? '', undefined, { sensitivity: 'base' });
    }
    return dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}

// Kategorielose Schritte ans Ende (höchster Rang), damit die Sortierung stabil bleibt.
function categoryRank(category: StepCategory | null): number {
  return category ? CATEGORY_RANK[category] : CATEGORY_ORDER.length;
}

// Freitext-Werte (Autor) zu eindeutigen, alphabetisch sortierten Filter-Optionen
// verdichten; Label entspricht dem Wert. null wird verworfen.
function toLabelOptions(values: (string | null)[]): FilterOption[] {
  const distinct = [...new Set(values.filter((v): v is string => v !== null))].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
  );
  return distinct.map((value) => ({ value, label: value }));
}
